import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { PromptExtendedModel, AssistantTypeModel } from '../models/BusinessDataModel';
import type { PromptTagDefinition } from '../components/details/AiConfigNew/types';

export type RouteKey =
  | 'generalInfo'
  | 'newClaimHousing'
  | 'newClaimBusinessEnergy'
  | 'existingClaims'
  | 'generalKnowledge';

export interface ObjectiveConfig {
  title: string;
  prompt: string;
  steps: string[];              // ordered list
  selectedFunctions: string[];  // ids/names of functions needed for this objective
}

export interface UploadedFileMeta {
  id: string;
  name: string;
  size: number;
  uploadedAt: string; // ISO
  url?: string;
}

export interface AIConfigState {
  prompts: PromptExtendedModel[];
  assistants: AssistantTypeModel[];
  promptTagDefinitions: PromptTagDefinition[];

  allowInterrupts: boolean;
  selectedVoice: string;
  greetingMessage: string;
  coreRules: string[];      // bullets
  coreFunctions: string[];  // enabled global functions
  routing: Record<RouteKey, ObjectiveConfig>;
  knowledge: {
    textBlocks: string[];       // arbitrary knowledge text
    files: UploadedFileMeta[];  // uploaded docs
  };
}

export const AVAILABLE_FUNCTIONS = [
  'warmTransfer',
  'sendSMS',
  'scheduleConsultation',
  'createLead',
  'sendDocLink',
  'verifyCustomer',
  'lookupPolicy',
] as const;

const initialState: AIConfigState = {
  prompts: [],
  assistants: [],
  promptTagDefinitions: [],

  allowInterrupts: false,
  selectedVoice: 'ash',
  greetingMessage: '',
  coreRules: [],
  coreFunctions: [],
  routing: {
    generalInfo: {
      title: 'General Info',
      prompt: '',
      steps: [],
      selectedFunctions: [],
    },
    newClaimHousing: {
      title: 'New Claim (Housing)',
      prompt: '',
      steps: [],
      selectedFunctions: [],
    },
    newClaimBusinessEnergy: {
      title: 'New Claim (Business Energy)',
      prompt: '',
      steps: [],
      selectedFunctions: [],
    },
    existingClaims: {
      title: 'Existing Claims',
      prompt: '',
      steps: [],
      selectedFunctions: [],
    },
    generalKnowledge: {
      title: 'General Knowledge',
      prompt: '',
      steps: [],
      selectedFunctions: [],
    },
  },
  knowledge: {
    textBlocks: [],
    files: [],
  },
};

const aiConfigSlice = createSlice({
  name: 'aiConfig',
  initialState,
  reducers: {
    // top-level
    setAllowInterrupts(state, action: PayloadAction<boolean>) {
      state.allowInterrupts = action.payload;
    },
    setSelectedVoice(state, action: PayloadAction<string>) {
      state.selectedVoice = action.payload;
    },
    setGreetingMessage(state, action: PayloadAction<string>) {
      state.greetingMessage = action.payload;
    },
    setCoreFunctions(state, action: PayloadAction<string[]>) {
      state.coreFunctions = action.payload;
    },

    setPromptsAndAssistants(state, action: PayloadAction<{ prompts: PromptExtendedModel[]; assistants: AssistantTypeModel[] }>) {
      state.prompts = action.payload.prompts;
      state.assistants = action.payload.assistants;
    },

    setPromptTagDefinitions(state, action: PayloadAction<PromptTagDefinition[]>) {
      state.promptTagDefinitions = action.payload ?? [];
    },

    // core rules
    setCoreRules(state, action: PayloadAction<string[]>) {
      state.coreRules = action.payload;
    },
    addCoreRule(state, action: PayloadAction<string>) {
      state.coreRules.push(action.payload);
    },
    updateCoreRule(state, action: PayloadAction<{ index: number; value: string }>) {
      const { index, value } = action.payload;
      if (state.coreRules[index] !== undefined) state.coreRules[index] = value;
    },
    removeCoreRule(state, action: PayloadAction<number>) {
      state.coreRules.splice(action.payload, 1);
    },
    moveCoreRule(state, action: PayloadAction<{ from: number; to: number }>) {
      const { from, to } = action.payload;
      if (from === to) return;
      const item = state.coreRules.splice(from, 1)[0];
      state.coreRules.splice(Math.max(0, Math.min(to, state.coreRules.length)), 0, item);
    },

    // objectives (routing)
    setObjectivePrompt(state, action: PayloadAction<{ route: RouteKey; prompt: string }>) {
      state.routing[action.payload.route].prompt = action.payload.prompt;
    },
    setObjectiveFunctions(
      state,
      action: PayloadAction<{ route: RouteKey; functions: string[] }>
    ) {
      state.routing[action.payload.route].selectedFunctions = action.payload.functions;
    },
    setObjectiveSteps(state, action: PayloadAction<{ route: RouteKey; steps: string[] }>) {
      state.routing[action.payload.route].steps = action.payload.steps;
    },
    addObjectiveStep(state, action: PayloadAction<{ route: RouteKey; value: string }>) {
      state.routing[action.payload.route].steps.push(action.payload.value);
    },
    updateObjectiveStep(
      state,
      action: PayloadAction<{ route: RouteKey; index: number; value: string }>
    ) {
      const { route, index, value } = action.payload;
      if (state.routing[route].steps[index] !== undefined)
        state.routing[route].steps[index] = value;
    },
    removeObjectiveStep(state, action: PayloadAction<{ route: RouteKey; index: number }>) {
      state.routing[action.payload.route].steps.splice(action.payload.index, 1);
    },
    moveObjectiveStep(
      state,
      action: PayloadAction<{ route: RouteKey; from: number; to: number }>
    ) {
      const { route, from, to } = action.payload;
      if (from === to) return;
      const arr = state.routing[route].steps;
      const item = arr.splice(from, 1)[0];
      arr.splice(Math.max(0, Math.min(to, arr.length)), 0, item);
    },

    // knowledge
    setKnowledgeTextBlocks(state, action: PayloadAction<string[]>) {
      state.knowledge.textBlocks = action.payload;
    },
    addKnowledgeTextBlock(state, action: PayloadAction<string>) {
      state.knowledge.textBlocks.push(action.payload);
    },
    updateKnowledgeTextBlock(state, action: PayloadAction<{ index: number; value: string }>) {
      const { index, value } = action.payload;
      if (state.knowledge.textBlocks[index] !== undefined)
        state.knowledge.textBlocks[index] = value;
    },
    removeKnowledgeTextBlock(state, action: PayloadAction<number>) {
      state.knowledge.textBlocks.splice(action.payload, 1);
    },

    addKnowledgeFile(state, action: PayloadAction<UploadedFileMeta>) {
      state.knowledge.files.push(action.payload);
    },
    removeKnowledgeFile(state, action: PayloadAction<string>) {
      state.knowledge.files = state.knowledge.files.filter(f => f.id !== action.payload);
    },

    // bulk / persistence
    loadAiConfig(state, action: PayloadAction<AIConfigState>) {
      return action.payload;
    },
    resetAiConfig() {
      return initialState;
    },
  },
});

export const {
  setPromptsAndAssistants,
  setPromptTagDefinitions,
  setAllowInterrupts,
  setGreetingMessage,
  setCoreFunctions,
  setCoreRules,
  addCoreRule,
  updateCoreRule,
  removeCoreRule,
  moveCoreRule,
  setObjectivePrompt,
  setObjectiveFunctions,
  setObjectiveSteps,
  addObjectiveStep,
  updateObjectiveStep,
  removeObjectiveStep,
  moveObjectiveStep,
  setKnowledgeTextBlocks,
  addKnowledgeTextBlock,
  updateKnowledgeTextBlock,
  removeKnowledgeTextBlock,
  addKnowledgeFile,
  removeKnowledgeFile,
  setSelectedVoice,
  loadAiConfig,
  resetAiConfig,
} = aiConfigSlice.actions;

export const selectAiConfig = (s: RootState) => s.aiConfig;
export const selectObjective = (route: RouteKey) => (s: RootState) => s.aiConfig.routing[route];
export const selectPrompts = (state: RootState) => state.aiConfig.prompts;
export const selectAssistants = (state: RootState) => state.aiConfig.assistants;
export const selectPromptTagDefinitions = (state: RootState) => state.aiConfig.promptTagDefinitions;
export const selectSelectedVoice = (state: RootState) => state.aiConfig.selectedVoice;

export default aiConfigSlice.reducer;
